/*
 * camera.ts - the map camera: its definition, the terrain under it, and
 * the physical geometry derived from both
 */

import * as math from '../utils/math';
import { mat4, vec3, vec4 } from '../utils/matrix';
import * as illumination from './illumination';
import type Map from './map';
import type LegacyMap from './legacy-map';
import type VerticalExaggeration from './vertical-exaggeration';


/**
 * The camera of a map, owned by `Map` as `map.camera`.
 *
 * The definition is what a position encodes: a center in the navigation
 * SRS, an altitude with the reference it is measured from, the three
 * Euler angles of the optical axis, the vertical view extent at the
 * center, and the vertical field of view. `set` is the one writer; the
 * operations `pan`, `rotate`, `setAltitudeRef` and `constrain` go
 * through it.
 *
 * The projection parameters are camera state outside the definition:
 * the render target's aspect ratio, which `Map.tick` sets from the
 * canvas, and whether the projection is orthographic.
 *
 * `geometry` is the physical-SRS derivation of the definition, the
 * projection parameters, the terrain height under the center and the
 * vertical exaggeration: eye, camera axes, distances, near and far
 * planes, the view and projection matrices and the frustum planes. It
 * is cached and rebuilt on the first read after any of those changes.
 * The vertical exaggeration is the one input the camera does not own:
 * `Map.tick` tells it the generation of the vertical exaggeration
 * configuration and the view extent it follows, the selection
 * camera's. The visibility and scale queries read the geometry.
 *
 * The terrain heights come from the elevation store. The camera keeps a
 * terrain sample set registered with the map for the segment from the
 * center to the eye's footprint; `update`, once per map tick, accepts
 * the heights the store has answered so far, the center's first. A
 * camera obtained from `clone` keeps the heights and the projection
 * parameters it was cloned with and is never updated; freeze mode holds
 * one as the selection camera.
 */
class MapCamera {

    /**
     * @param map the map that owns the camera
     * @param verticalExaggeration the map's vertical exaggeration
     */
    constructor(map: Map, verticalExaggeration: VerticalExaggeration) {

        this.map_ = map;
        this.verticalExaggeration_ = verticalExaggeration;
    }

    // -----------------------------------------------------------------
    // Definition
    // -----------------------------------------------------------------

    /**
     * Replaces the given parts of the definition. Pitch is clamped to
     * the vertical, yaw and roll are wrapped, and the geometry is
     * rebuilt on its next read.
     */
    set(definition: Partial<MapCamera.Definition>): this {

        const current = this.definition_;

        this.definition_ = {
            center: definition.center
                ? [definition.center[0], definition.center[1]]
                : current.center,
            altitude: definition.altitude ?? current.altitude,
            altitudeRef: definition.altitudeRef ?? current.altitudeRef,
            yaw: (definition.yaw ?? current.yaw) % 360,
            pitch: math.clamp(definition.pitch ?? current.pitch, -90, 90),
            roll: (definition.roll ?? current.roll) % 360,
            viewExtent: definition.viewExtent ?? current.viewExtent,
            fovy: definition.fovy ?? current.fovy,
        };

        this.invalidate();
        this.legacyMap().markDirty();
        return this;
    }

    /** A copy of the definition. */
    get definition(): MapCamera.Definition {

        const current = this.definition_;

        return {
            ...current,
            center: [current.center[0], current.center[1]],
        };
    }

    /** Center in the navigation SRS. */
    get center(): readonly [number, number] {

        return this.definition_.center;
    }

    /** Altitude of the center, measured from `altitudeRef`. */
    get altitude(): number {

        return this.definition_.altitude;
    }

    /** What the altitude is measured from. */
    get altitudeRef(): MapCamera.AltitudeRef {

        return this.definition_.altitudeRef;
    }

    /** Heading of the optical axis in degrees, clockwise from north. */
    get yaw(): number {

        return this.definition_.yaw;
    }

    /**
     * Elevation of the optical axis in degrees: -90 looks straight down,
     * 0 at the horizon.
     */
    get pitch(): number {

        return this.definition_.pitch;
    }

    /**
     * Rotation about the optical axis in degrees; positive turns the
     * screen's up direction toward its right.
     */
    get roll(): number {

        return this.definition_.roll;
    }

    /** Vertical extent of the view at the center, in metres. */
    get viewExtent(): number {

        return this.definition_.viewExtent;
    }

    /** Vertical field of view in degrees. */
    get fovy(): number {

        return this.definition_.fovy;
    }

    /** Distance from the eye to the center that shows `viewExtent`. */
    get viewDistance(): number {

        return this.definition_.viewExtent * 0.5
            / Math.tan(math.radians(this.definition_.fovy * 0.5));
    }

    /**
     * Whether a definition describes this camera within the tolerances
     * a position round trip keeps.
     */
    equals(definition: MapCamera.Definition): boolean {

        const current = this.definition_;

        return current.altitudeRef === definition.altitudeRef
            && math.isEqual(current.center[0], definition.center[0], 1e-7)
            && math.isEqual(current.center[1], definition.center[1], 1e-7)
            && math.isEqual(current.altitude, definition.altitude, 1e-3)
            && math.isEqual(current.yaw, definition.yaw, 1e-3)
            && math.isEqual(current.pitch, definition.pitch, 1e-3)
            && math.isEqual(current.roll, definition.roll, 1e-3)
            && math.isEqual(current.viewExtent, definition.viewExtent, 1e-3)
            && math.isEqual(current.fovy, definition.fovy, 1e-3);
    }

    /**
     * The definition with its altitude measured from another reference,
     * so the camera stays where it is. Uses the terrain height under the
     * center accepted so far.
     */
    withAltitudeRef(altitudeRef: MapCamera.AltitudeRef): MapCamera.Definition {

        const definition = this.definition;
        if (definition.altitudeRef === altitudeRef) return definition;

        const terrain = this.terrainAltitude_;

        definition.altitude += altitudeRef === 'datum' ? terrain : -terrain;
        definition.altitudeRef = altitudeRef;
        return definition;
    }

    /** Changes the altitude reference without moving the camera. */
    setAltitudeRef(altitudeRef: MapCamera.AltitudeRef): this {

        return this.set(this.withAltitudeRef(altitudeRef));
    }

    /**
     * Moves the center horizontally; the altitude and its reference stay.
     *
     * @param azimuth direction of travel in degrees, clockwise from north
     * @param distance distance in metres along the ellipsoid geodesic,
     *   or in the projection plane on a projected frame
     * @param yawCorrection whether the yaw is corrected by the
     *   geodesic's change of azimuth, keeping the heading relative to
     *   the path; otherwise the yaw stays a fixed compass heading
     */
    pan(azimuth: number, distance: number, yawCorrection = true): this {

        const [x, y] = this.definition_.center;

        if (!this.legacyMap().isGeocent) {

            const direction = math.radians(azimuth);

            return this.set({
                center: [x - Math.sin(direction) * distance,
                    y + Math.cos(direction) * distance],
            });
        }

        // the library's own typing omits the destination fields
        const path = this.geodesic().Direct(y, x, azimuth, distance);

        return this.set({
            center: [path.lon2!, path.lat2!],
            yaw: yawCorrection
                ? this.definition_.yaw - (path.azi1 - path.azi2!)
                : this.definition_.yaw,
        });
    }

    /** Turns the camera by the given angle deltas in degrees. */
    rotate(yaw: number, pitch: number, roll: number): this {

        const current = this.definition_;

        return this.set({
            yaw: current.yaw + yaw,
            pitch: current.pitch + pitch,
            roll: current.roll + roll,
        });
    }

    /**
     * Keeps the camera within the given limits: the view extent inside
     * its range, the tilt within what the distance to the horizon
     * allows, and the eye clear of the terrain under it.
     *
     * The clearance rule samples the terrain along the segment from the
     * center to the eye's footprint. Each sample is the footprint of the
     * eye at one pitch between straight down and the current one, so the
     * least steep sampled pitch whose eye clears the terrain by the
     * required distance is the answer, interpolated between neighbouring
     * samples. Samples the terrain cannot answer for skip the rule.
     */
    constrain(limits: MapCamera.Limits): this {

        const current = this.definition_;

        //clamp view extets
        const viewExtent = math.clamp(current.viewExtent,
            limits.minViewExtent, limits.maxViewExtent);

        this.set({ viewExtent });

        const viewDistance = this.viewDistance;
        const halfFovy = math.radians(current.fovy * 0.5);
        const srsInfo = this.legacyMap().getNavigationSrs().getSrsInfo();
        const bodyRadius = srsInfo['a'] as number | undefined;

        if (!bodyRadius) return this;

        //reduce tilt when you are far off the planet
        const horizon = Math.asin(
            bodyRadius / (viewDistance + bodyRadius)); //get angle to horion
        let factor = Math.tan(halfFovy) / Math.tan(horizon);

        const [thresholdMin, thresholdMax] = limits.tiltConstrainThreshold;
        let maxTilt = 20;

        if (thresholdMin < thresholdMax) {

            factor = math.clamp(factor, thresholdMin, thresholdMax);
            factor = (factor - thresholdMin) / (thresholdMax - thresholdMin);
            maxTilt = 20 + (-90 - 20) * factor;
        }

        this.set({ pitch: math.clamp(current.pitch, -90, maxTilt) });

        // the clearance the eye must keep above the terrain under it:
        // half the closest view distance, or three degrees of the
        // current one, whichever is larger
        const closestDistance = limits.minViewExtent * 0.5
            / Math.tan(halfFovy) * 0.5;
        const clearance = Math.max(closestDistance,
            viewDistance * Math.tan(math.radians(3)));

        const samples = this.sampleClearance(SampleCount);
        if (!samples) return this;

        // the current pitch clears the terrain
        if (samples[SampleCount].clearance >= clearance) return this;

        // find the least steep sampled pitch that does, and interpolate
        // toward the next one
        for (let index = SampleCount - 1; index >= 0; index--) {

            const steeper = samples[index];
            if (steeper.clearance < clearance) continue;

            const shallower = samples[index + 1];
            const span = steeper.clearance - shallower.clearance;
            const fraction = span > 0
                ? (steeper.clearance - clearance) / span : 0;

            return this.set({
                pitch: steeper.pitch
                    + (shallower.pitch - steeper.pitch) * fraction,
            });
        }

        return this.set({ pitch: samples[0].pitch });
    }

    /** A still copy of the camera: its definition, projection parameters
     * and terrain heights. */
    clone(): MapCamera {

        const copy = new MapCamera(this.map_, this.verticalExaggeration_);

        copy.definition_ = this.definition;
        copy.projection_ = this.projection;
        copy.terrainAltitude_ = this.terrainAltitude_;
        copy.terrainHeights_ = this.terrainHeights_.slice();
        copy.veGeneration_ = this.veGeneration_;
        copy.veExtent_ = this.veExtent_;
        return copy;
    }

    // -----------------------------------------------------------------
    // Projection
    // -----------------------------------------------------------------

    /**
     * Replaces the given projection parameters. A position never carries
     * them, and a clone keeps the ones it was cloned with.
     */
    setProjection(projection: Partial<MapCamera.Projection>): this {

        const current = this.projection_;
        const shift = projection.lensShift ?? current.lensShift;

        const next: MapCamera.Projection = {
            aspect: projection.aspect ?? current.aspect,
            ortho: projection.ortho ?? current.ortho,
            lensShift: [...shift],
            cropFactor: projection.cropFactor ?? current.cropFactor,
        };

        if (next.aspect === current.aspect
                && next.ortho === current.ortho
                && next.lensShift[0] === current.lensShift[0]
                && next.lensShift[1] === current.lensShift[1]
                && next.cropFactor === current.cropFactor) return this;

        this.projection_ = next;
        this.invalidate();
        this.legacyMap().markDirty();
        return this;
    }

    /** A copy of the projection parameters. */
    get projection(): MapCamera.Projection {

        const current = this.projection_;

        return {
            ...current,
            lensShift: [...current.lensShift],
        };
    }

    // -----------------------------------------------------------------
    // Terrain
    // -----------------------------------------------------------------

    /** The accepted terrain height under the center, measured from the
     * `datum` reference of `AltitudeRef`. */
    get terrainAltitude(): number {

        return this.terrainAltitude_;
    }

    /**
     * Whether the store has answered every segment probe at the
     * requested resolution for the camera's current geometry: the one
     * registered set is settled and no newer one is pending.
     */
    get terrainSettled(): boolean {

        const sets = this.sampleSets_;
        return sets.length === 1 && sets[0].settled;
    }

    /**
     * Accepts the terrain heights the store has answered for the segment
     * samples, and re-registers them when the geometry moved them. A
     * sample without an answer yet keeps the height last accepted for
     * it. Called once per map tick before the frame is drawn; a changed
     * center height marks the map dirty.
     */
    update(): void {

        const sets = this.sampleSets_;
        const heights = this.terrainHeights_;
        let answered = -1;

        // oldest first, so the newest answer wins
        for (let setIndex = 0; setIndex < sets.length; setIndex++) {

            const samples = sets[setIndex].sampleHeight;

            for (let index = 0; index <= SampleCount; index++) {

                const height = samples[index];
                if (Number.isNaN(height)) continue;

                heights[index] = height;
                answered = setIndex;
            }
        }

        // a set older than the newest answered one is released; the
        // newer ones stay registered until the store answers them
        for (let setIndex = 0; setIndex < answered; setIndex++)
            this.map_.releaseRegisteredSamples(sets[setIndex]);

        if (answered > 0) sets.splice(0, answered);

        const stats = this.legacyMap().stats;

        stats.heightTerrain = this.terrainAltitude_;
        stats.heightDelta = this.definition_.altitude;

        if (!Number.isNaN(heights[0]) && heights[0] !== this.terrainAltitude_) {

            this.terrainAltitude_ = heights[0];
            this.invalidate();
            this.legacyMap().markDirty();
        }

        this.updateSamples();
    }

    // -----------------------------------------------------------------
    // Geometry
    // -----------------------------------------------------------------

    /**
     * Tells the camera the vertical exaggeration to derive the geometry
     * with: the generation of the vertical exaggeration configuration
     * and the view extent its scale follows, the selection camera's. A
     * change rebuilds the geometry on its next read.
     */
    setVerticalExaggeration(generation: number, viewExtent: number): this {

        if (generation === this.veGeneration_
                && viewExtent === this.veExtent_) return this;

        this.veGeneration_ = generation;
        this.veExtent_ = viewExtent;
        this.invalidate();
        return this;
    }

    /**
     * The physical-SRS geometry of the view. Cached; a read after a
     * change to the definition, the projection parameters, the accepted
     * terrain height, or the vertical exaggeration derives it again.
     */
    get geometry(): MapCamera.Geometry {

        if (!this.geometry_) this.geometry_ = Object.freeze(this.derive());
        return this.geometry_;
    }

    /**
     * The derivative of the screen x position in normalized device coordinates
     * as a function of the view-space x coordinate in meters.
     *
     * The distance from the eye stands in for the view depth, which was
     * intendeded to keep the tile resolution independent of a fixed-camera
     * rotation. This comes at a cost of accuracy, the value underestimates
     * towards the edge of the frustum, it would be worth measuring whether
     * this pays off.
     *
     * Current exclusive consumer: the MapSurfaceTile getPixelSize
     * method, indirectly also updateTexelSize.
     *
     * @param point position relative to the eye
     * @param returnDistance whether to return the distance as well
     * @returns the factor, or `[factor, distance]`; infinite inside the
     *   near plane
     */
    ndcUnitPerViewUnit(
        point: math.vec3, returnDistance = false,
    ): number | [number, number] {

        // the modelview is a rotation, so the distance from the eye is
        // the point's own length
        const distance = vec3.length(point);
        const factor = this.ndcUnitPerViewUnitAtDistance(distance);

        return returnDistance ? [factor, distance] : factor;
    }

    /**
     * `ndcUnitPerViewUnit` at a known distance from the eye.
     *
     * Current consumers: the MapSurfaceTile getPixelSize3 method, and
     * the label sizing in the renderer's draw and rmap modules.
     */
    ndcUnitPerViewUnitAtDistance(distance: number): number {

        const geometry = this.geometry;

        // an orthographic projection scales every depth alike
        if (this.projection_.ortho) return geometry.projection[0];

        if (distance < geometry.near) return Number.POSITIVE_INFINITY;

        // the expression "projection(0,0) / depth" is the derivative of the
        // screen X position by the camera space X coordinate.

        // ('dist' is used instead of camera depth (camPos(2)) to make the tile
        // resolution independent of camera rotation)
        return geometry.projection[0] / distance;
    }

    /**
     * Whether the convex hull of the points may intersect the frustum:
     * false only when one frustum plane has every point on its outer
     * side.
     *
     * @param points physical positions, three numbers each
     * @param shift subtracted from every point: the eye the frustum
     *   planes are relative to
     */
    pointsVisible(points: ArrayLike<number>, shift?: math.vec3): boolean {

        const planes = this.geometry.frustumPlanes;
        const [sx, sy, sz] = shift ?? [0, 0, 0];

        for (let i = 0; i < 6; i++) {

            const plane = planes[i];
            let negative = true;

            for (let j = 0; j < points.length; j += 3) {

                if (vec4.dot3(plane, points, j, sx, sy, sz) >= 0) {
                    negative = false;
                    break;
                }
            }

            if (negative) return false;
        }

        return true;
    }

    /**
     * Whether a box may intersect the frustum: false only when one
     * frustum plane has all eight corners on its outer side.
     *
     * @param bbox the box's `min` and `max` corners in the physical SRS
     * @param shift subtracted from the corners: the eye the frustum
     *   planes are relative to
     */
    bboxVisible(bbox: MapCamera.BBox, shift?: math.vec3): boolean {

        const [sx, sy, sz] = shift ?? [0, 0, 0];
        const minX = bbox.min[0] - sx, maxX = bbox.max[0] - sx;
        const minY = bbox.min[1] - sy, maxY = bbox.max[1] - sy;
        const minZ = bbox.min[2] - sz, maxZ = bbox.max[2] - sz;

        const points = BoxCorners;

        for (let j = 0; j < 8; j++) {

            const p = points[j];
            p[0] = j & 4 ? maxX : minX;
            p[1] = j & 2 ? maxY : minY;
            p[2] = j & 1 ? maxZ : minZ;
        }

        const planes = this.geometry.frustumPlanes;

        for (let i = 0; i < 6; i++) {

            const plane = planes[i];
            let negative = true;

            for (let j = 0; j < 8; j++) {

                if (vec4.dot2(plane, points[j]) >= 0) {
                    negative = false;
                    break;
                }
            }

            if (negative) return false;
        }

        // the test is conservative: a box whose corners straddle several
        // planes passes even when it lies entirely outside the frustum,
        // past one of its corners. An exact separating-axis test would
        // reject those - TODO
        return true;
    }

    // -----------------------------------------------------------------
    // Private
    // -----------------------------------------------------------------

    /** Forgets the cached geometry; the registered samples follow it. */
    private invalidate(): void {

        this.geometry_ = null;
        this.samplesStale_ = true;
    }

    /**
     * Derives the geometry from the definition, the accepted terrain
     * height, and the vertical exaggeration at the view extent it was
     * told, the scale terrain is exaggerated at.
     */
    private derive(): MapCamera.Geometry {

        const definition = this.definition_;
        const legacyMap = this.legacyMap();
        const projected = !legacyMap.isGeocent;
        const terrain = this.terrainAltitude_;

        // altitude of the center from the datum, exaggerated as terrain is
        const altitude = definition.altitude
            + (definition.altitudeRef === 'terrain' ? terrain : 0);
        const exaggerated = this.verticalExaggeration_.apply(
            altitude, this.veExtent_);

        const center = legacyMap.convert.convertCoords(
            [definition.center[0], definition.center[1], exaggerated],
            'navigation', 'physical') as math.vec3;

        // camera axes in the NED frame at the center: in lNED they are
        // the unit axes, forward N, right E and up -D
        const orientation = {
            yaw: definition.yaw,
            pitch: definition.pitch,
            roll: definition.roll,
        };

        const forwardNed = illumination.lned2ned([1, 0, 0], orientation);
        const rightNed = illumination.lned2ned([0, 1, 0], orientation);
        const upNed = illumination.lned2ned([0, 0, -1], orientation);

        // the same axes in the physical SRS
        let forward: math.vec3, right: math.vec3, up: math.vec3;

        if (projected) {

            forward = nedToProjected(forwardNed);
            right = nedToProjected(rightNed);
            up = nedToProjected(upNed);

        } else {

            const [longitude, latitude] = definition.center;
            forward = illumination.ned2ecef(forwardNed, longitude, latitude);
            right = illumination.ned2ecef(rightNed, longitude, latitude);
            up = illumination.ned2ecef(upNed, longitude, latitude);
        }

        // the eye sits behind the center along the optical axis
        const viewDistance = this.viewDistance;
        const eye = axpby(1, center, -viewDistance, forward);

        const eyeAltitude = exaggerated
            + viewDistance * Math.sin(-math.radians(definition.pitch));
        const heightAboveTerrain = eyeAltitude - terrain;
        const distance = Math.max(heightAboveTerrain, viewDistance);

        //set near and far of camera by distance of orbit
        let factor = Math.max(eyeAltitude, distance) / 600000;
        const near = Math.max(NearPlane, NearPlane * factor * 20);
        factor = Math.max(1, factor);
        const far = 600000 * factor * 10 * 2;

        // world-to-view rotation: view x is right, y is up, z is back.
        // It is the whole modelview: draw sites subtract the eye from
        // world coordinates before transforming to avoid the numerical
        // instability of large coordinate values.
        const modelview = [
            right[0], up[0], -forward[0], 0,
            right[1], up[1], -forward[1], 0,
            right[2], up[2], -forward[2], 0,
            0, 0, 0, 1,
        ] as math.mat4;

        const parameters = this.projection_;
        let projection = (parameters.ortho
            ? math.orthographicMatrix(
                definition.viewExtent, parameters.aspect, near, far)
            : math.perspectiveMatrix(
                definition.fovy * 0.5, parameters.aspect, near, far)
        ) as math.mat4;

        // the crop factor shrinks the frame about the optical axis and the
        // lens shift then moves it over the image plane, a shift of one
        // frame width being two units of normalized device coordinates.
        // Both apply after the projection so that the frustum planes
        // follow
        const [shiftX, shiftY] = parameters.lensShift;
        const crop = parameters.cropFactor;

        if (crop !== 1)
            projection = mat4.multiply(
                math.scaleMatrix(crop, crop, 1),
                projection, mat4.create()) as math.mat4;

        if (shiftX !== 0 || shiftY !== 0)
            projection = mat4.multiply(
                math.translationMatrix(-2 * shiftX, -2 * shiftY, 0),
                projection, mat4.create()) as math.mat4;

        const mvp = mat4.multiply(
            projection, modelview, mat4.create()) as math.mat4;

        // transform the frustum planes to the view space. Planes in homogeneous
        // coordinates transform as p' = M^{-T} * p, where (using the transpose
        // of inverse of M)
        const mvpTransposed = mat4.transpose(mvp, mat4.create());
        const frustumPlanes = ClipPlanes.map((plane) =>
            mat4.multiplyVec4(mvpTransposed, plane, [0, 0, 0, 0]));

        const eyeNavigation = legacyMap.convert.convertCoords(
            eye, 'physical', 'navigation') as math.vec3;

        return {
            eye,
            center,
            forward,
            right,
            up,
            modelview,
            projection,
            mvp,
            modelviewInverse: mat4.inverse(modelview, mat4.create()),
            projectionInverse: mat4.inverse(projection, mat4.create()),
            mvpInverse: mat4.inverse(mvp, mat4.create()),
            frustumPlanes,
            modelview32: new Float32Array(modelview),
            projection32: new Float32Array(projection),
            mvp32: new Float32Array(mvp),
            eyeXY: [eyeNavigation[0], eyeNavigation[1]],
            heightAboveTerrain,
            distance: math.clamp(distance, 0.1, far),
            near,
            far,
            eyeDistance: vec3.length(eye),
            eyeNormal: vec3.normalize(eye, [0, 0, 0]),
        };
    }

    /**
     * The eye's clearance above the terrain at `count + 1` pitches from
     * straight down to the current pitch, from the accepted heights of
     * the segment samples. Sample `index` is the eye's footprint at the
     * fraction `index / count` of its horizontal offset from the center,
     * and the pitch that puts it there.
     *
     * @returns the samples, or null while one of them has no height yet
     */
    private sampleClearance(count: number): ClearanceSample[] | null {

        const definition = this.definition_;
        const pitch = math.radians(definition.pitch);
        const viewDistance = this.viewDistance;
        const centerAltitude = definition.altitude
            + (definition.altitudeRef === 'terrain'
                ? this.terrainAltitude_ : 0);

        const samples: ClearanceSample[] = [];

        for (let index = 0; index <= count; index++) {

            const fraction = index / count;
            const terrain = this.terrainHeights_[index];

            if (Number.isNaN(terrain)) return null;

            // the current pitch itself for the last sample; the pitch
            // whose footprint is at this fraction of the offset otherwise
            const samplePitch = index === count
                ? pitch : -Math.acos(fraction * Math.cos(pitch));
            const eyeAltitude = centerAltitude
                + viewDistance * Math.sin(-samplePitch);

            samples.push({
                pitch: math.degrees(samplePitch),
                clearance: eyeAltitude - terrain,
            });
        }

        return samples;
    }

    /**
     * Registers the segment samples anew when the geometry moved them:
     * `SampleCount + 1` probes on the chord from the center, at index
     * 0, to the eye, the last being the eye's footprint. A moving
     * camera registers a set every frame; `update` releases each once
     * a newer one is answered, so no answer is lost and the accepted
     * heights follow the terrain continuously.
     */
    private updateSamples(): void {

        if (!this.samplesStale_) return;
        this.samplesStale_ = false;

        const geometry = this.geometry;
        const gsd = this.sampleGsd();
        const points: math.vec3[] = [];

        for (let index = 0; index <= SampleCount; index++) {

            const fraction = index / SampleCount;
            points.push(axpby(1 - fraction, geometry.center,
                fraction, geometry.eye));
        }

        // an accepted height rebuilds the geometry without moving the
        // probes; the registration stands then
        const registered = this.probePoints_;

        if (registered && gsd === this.probeGsd_
                && points.every((point, index) =>
                    point[0] === registered[index][0]
                    && point[1] === registered[index][1]
                    && point[2] === registered[index][2]))
            return;

        const convert = this.legacyMap().convert;
        const probes = points.map((point) => {

            const [x, y] = convert.convertCoords(
                point, 'physical', 'navigation');

            return [x, y] as [number, number];
        });

        this.sampleSets_.push(this.map_.registerSamples(probes, gsd));
        this.probePoints_ = points;
        this.probeGsd_ = gsd;
    }

    /**
     * The ground sample distance the segment samples ask for: a fraction
     * of the view extent, coarse on purpose, so the camera follows the
     * terrain's average and not every bump when it pans.
     */
    private sampleGsd(): number {

        return this.definition_.viewExtent
            / this.legacyMap().config.mapNavSamplesPerViewExtent;
    }

    /** The geodesic of the navigation ellipsoid, created on first use. */
    private geodesic(): MapCamera.Geodesic {

        if (!this.geodesic_)
            this.geodesic_ = this.legacyMap().measure.getGeodesic();

        return this.geodesic_;
    }

    /** The map's legacy half. Read fresh on every call rather than
     * captured at construction, since the camera is constructed before
     * the style finishes loading and legacy map object it wraps exists. */
    private legacyMap(): LegacyMap {

        return this.map_.getMap()!;
    }

    private readonly map_: Map;
    private readonly verticalExaggeration_: VerticalExaggeration;

    private definition_: MapCamera.Definition = {
        center: [0, 0],
        altitude: 0,
        altitudeRef: 'datum',
        yaw: 0,
        pitch: -90,
        roll: 0,
        viewExtent: 900,
        fovy: 45,
    };

    private projection_: MapCamera.Projection = {
        aspect: 1,
        ortho: false,
        lensShift: [0, 0],
        cropFactor: 1,
    };

    private terrainAltitude_ = 0;

    /** The accepted heights of the segment samples, NaN until answered. */
    private terrainHeights_ = new Float64Array(SampleCount + 1).fill(NaN);

    /** The registered segment sample sets, oldest first, and whether
     * the geometry moved since the newest was placed. */
    private readonly sampleSets_: Map.TerrainSamples[] = [];
    private samplesStale_ = true;

    /** The physical positions and cell size the newest set was
     * registered at. */
    private probePoints_: math.vec3[] | null = null;
    private probeGsd_ = 0;

    private geometry_: MapCamera.Geometry | null = null;

    /** The vertical exaggeration the geometry is derived with, as told
     * by `setVerticalExaggeration`. */
    private veGeneration_ = -1;
    private veExtent_ = 0;
    private geodesic_: MapCamera.Geodesic | null = null;
}


/** Number of intervals the clearance rule samples between the center
 * and the eye's footprint. */
const SampleCount = 8;

/** Nearest allowed near plane, in metres. */
const NearPlane = 2;

// prepare frustum planes (in normalized device coordinates)
const ClipPlanes: readonly math.vec4[] = [
    [ 0, 0, 1, 1 ], // far
    [ 0, 0,-1, 1 ], // near
    [ 1, 0, 0, 1 ], // left
    [-1, 0, 0, 1 ], // right
    [ 0, 1, 0, 1 ], // bottom
    [ 0,-1, 0, 1 ], // top
];

//reduce garbage collection
const BoxCorners: math.vec4[] = Array.from(
    { length: 8 }, () => [0, 0, 0, 1]);


/**
 * Convert a NED vector into the physical SRS of a projected map, whose
 * axes point east, north and up.
 */
function nedToProjected(arg: math.vec3): math.vec3 {
    return [arg[1], arg[0], -arg[2]];
}


/** `a * x + b * y` for three-component vectors. */
function axpby(
    a: number, x: ArrayLike<number>, b: number, y: ArrayLike<number>,
): math.vec3 {

    return [a * x[0] + b * y[0], a * x[1] + b * y[1], a * x[2] + b * y[2]];
}


// export types

namespace MapCamera {

    /**
     * What an altitude is measured from. `datum` is the ellipsoid of
     * the navigation SRS on a geocentric frame, so the altitude is the
     * geodetic (ellipsoidal) height; on a projected frame it is the
     * navigation SRS's Z axis. It is never a geoid. `terrain` is the
     * terrain height under the center, itself datum-referenced.
     */
    export type AltitudeRef = 'datum' | 'terrain';

    /** The values that define the camera; what a position encodes. */
    export type Definition = {
        center: readonly [number, number];
        altitude: number;
        altitudeRef: AltitudeRef;
        yaw: number;
        pitch: number;
        roll: number;
        viewExtent: number;
        fovy: number;
    };

    /** The three angles of the optical axis, in degrees. */
    export type Orientation = {
        readonly yaw: number;
        readonly pitch: number;
        readonly roll: number;
    };

    /** The projection parameters outside the definition. */
    export type Projection = {

        /** Width over height of the render target. */
        aspect: number;

        /** Whether the projection is orthographic, at `viewExtent`. */
        ortho: boolean;

        /**
         * Displacement of the frame over the image plane, in fractions
         * of the frame's own width and height: `[0, 0]` is centred on
         * the optical axis, `[1, 0]` is the frame one frame width to the
         * right of it, `[0, 1]` one frame height above.
         */
        lensShift: readonly [number, number];

        /**
         * How many times smaller than the field of view the frame is: 1
         * shows the whole field, 2 halves the frame in each direction at
         * twice the image scale, the lens shift placing the frame. A
         * render loop that tiles a large image sets a crop factor of the
         * grid size and a lens shift per tile; the definition, and with
         * it the lighting, stay those of the whole image.
         */
        cropFactor: number;
    };

    /** An axis-aligned box in the physical SRS. */
    export type BBox = {
        min: ArrayLike<number>;
        max: ArrayLike<number>;
    };

    /**
     * The derivation of the definition and the projection parameters.
     * Coordinates are in the physical SRS unless stated otherwise.
     */
    export type Geometry = Readonly<{

        /** Eye position. */
        eye: math.vec3;

        /** The point looked at. */
        center: math.vec3;

        /** Unit optical axis, from the eye toward the center. */
        forward: math.vec3;

        /** Unit screen-right axis. */
        right: math.vec3;

        /** Unit screen-up axis. */
        up: math.vec3;

        /** Model-view matrix with the eye at the origin */
        modelview: math.mat4;

        /** View-to-clip matrix. */
        projection: math.mat4;

        /** `projection` times `modelview`. */
        mvp: math.mat4;

        modelviewInverse: math.mat4;
        projectionInverse: math.mat4;
        mvpInverse: math.mat4;

        /** The six frustum planes relative to the eye, each `[a, b, c,
         * d]` with the inside on the positive side. */
        frustumPlanes: readonly math.vec4[];

        /** The matrices as shader uniforms. */
        modelview32: Float32Array;
        projection32: Float32Array;
        mvp32: Float32Array;

        /** The eye in the navigation SRS, without its height. */
        eyeXY: readonly [number, number];

        /** Eye altitude above the terrain height under the center. */
        heightAboveTerrain: number;

        /** Eye distance used for tile selection: the larger of the
         * height above terrain and the view distance. */
        distance: number;

        /** Near plane distance. */
        near: number;

        /** Far plane distance. */
        far: number;

        /** Distance of the eye from the origin of the physical SRS. */
        eyeDistance: number;

        /** Unit direction of the eye from the origin of the physical SRS.
         * Sole consumer of it and of `eyeDistance`: `getPixelSize3`. */
        eyeNormal: math.vec3;
    }>;

    /** Limits `constrain` keeps the camera within. */
    export type Limits = {
        minViewExtent: number;
        maxViewExtent: number;

        /** The band of view-to-horizon ratios over which the allowed
         * tilt narrows from 20 degrees above the horizon to nadir. */
        tiltConstrainThreshold: readonly [number, number];
    };

    /**
     * The GeographicLib geodesic the pan operation moves along. The
     * result shape follows the library's typing, which declares the
     * start of the path and leaves out the destination it also returns.
     */
    export type Geodesic = {
        Direct(
            lat1: number, lon1: number, azi1: number, s12: number,
        ): {
            lat1: number;
            lon1: number;
            azi1: number;
            s12: number;
            a12: number;
            lat2?: number;
            lon2?: number;
            azi2?: number;
        };
    };
}

// local types

/** One sample of the clearance rule in `constrain`. */
type ClearanceSample = {
        pitch: number;
        clearance: number;
};

export default MapCamera;
